import { useCallback, useEffect, useState } from "react";
import { X, ImageIcon, Pencil, BookOpenText, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopHeader from "@/components/TopHeader";
import AuthImage from "@/components/AuthImage";
import MicRecorder from "@/components/MicRecorder";
import PhotoPicker from "@/components/PhotoPicker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, todayBerlin, formatHoursShort } from "@/lib/format";

export default function Bautagebuch() {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [entries, setEntries] = useState([]);
    const [timeEntries, setTimeEntries] = useState([]);
    const [users, setUsers] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try {
            const [p, d, t, u] = await Promise.all([
                api.get("/projects"), api.get("/diary"),
                api.get("/time-entries"), api.get("/users"),
            ]);
            setProjects(p.data || []); setEntries(d.data || []);
            setTimeEntries(t.data || []); setUsers(u.data || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const projectName = (id) => projects.find((p) => p.id === id)?.name || "?";
    const userName = (id) => users.find((u) => u.id === id)?.name || "?";

    return (
        <div>
            <TopHeader title="Bautagebuch" />
            <main className="mx-auto max-w-2xl px-4 pt-4 safe-bottom" data-testid="page-bautagebuch">
                <div className="mb-3 flex justify-between">
                    <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">
                        {entries.length} Tageseinträge
                    </div>
                    <Button data-testid="btn-new-diary" onClick={() => setCreating(true)} className="mes-btn-primary h-10 px-4 text-[13px]">
                        <Plus className="mr-1 h-4 w-4" strokeWidth={2.4} /> Neu
                    </Button>
                </div>

                {entries.length === 0 && (
                    <Card className="mes-card border-dashed p-8 text-center text-sm text-mes-muted">
                        <BookOpenText className="mx-auto mb-3 h-6 w-6 text-mes-muted" />
                        Noch keine Einträge. Legen Sie den ersten Bautag an.
                    </Card>
                )}

                <div className="space-y-3">
                    {entries.map((d) => {
                        const dayTimes = timeEntries.filter(
                            (te) => te.project_id === d.project_id && te.date === d.date && te.status !== "archived",
                        );
                        const present = {};
                        dayTimes.forEach((te) => {
                            present[te.user_id] = (present[te.user_id] || 0) + (te.duration_minutes || 0);
                        });
                        return (
                            <Card key={d.id} className="mes-card p-4" data-testid={`diary-${d.id}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-display text-[15px] font-semibold text-mes-ink">
                                            {projectName(d.project_id)}
                                        </div>
                                        <div className="text-xs tabular text-mes-muted">
                                            {formatDate(d.date + "T00:00:00")}
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="icon" data-testid={`btn-edit-diary-${d.id}`}
                                        onClick={() => setEditing(d)} className="text-mes-muted hover:text-mes-ink">
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                </div>

                                {Object.keys(present).length > 0 && (
                                    <div className="mt-3 rounded-md bg-mes-surfaceAlt p-2">
                                        <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">
                                            Anwesende Mitarbeiter
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-2">
                                            {Object.entries(present).map(([uid, min]) => (
                                                <span key={uid} className="rounded-full border border-mes-border bg-white px-3 py-1 text-xs font-medium text-mes-ink">
                                                    {userName(uid)}{" "}
                                                    <span className="tabular text-mes-brand">{formatHoursShort(min)}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {d.work_performed && <DiaryField label="Ausgeführte Arbeiten" value={d.work_performed} />}
                                {d.blockers && <DiaryField label="Behinderungen / Wartezeiten" value={d.blockers} />}
                                {d.additional_work && <DiaryField label="Zusatzarbeiten" value={d.additional_work} />}
                                {d.agreements && <DiaryField label="Vereinbarungen" value={d.agreements} />}

                                {d.photos?.length > 0 && (
                                    <div className="mt-3 grid grid-cols-3 gap-2">
                                        {d.photos.map((p) => (
                                            <div key={p.photo_id} className="relative aspect-square overflow-hidden rounded-md border border-mes-border">
                                                <AuthImage photoId={p.photo_id} className="h-full w-full object-cover" alt={p.caption || "Foto"} />
                                                {p.caption && (
                                                    <div className="absolute inset-x-0 bottom-0 bg-mes-ink/85 px-1 py-0.5 text-[10px] text-white line-clamp-1">
                                                        {p.caption}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </main>

            <DiaryDialog
                open={creating || !!editing}
                mode={editing ? "edit" : "create"}
                entry={editing}
                projects={projects.filter((p) => user?.role === "admin" || (p.assigned_user_ids || []).includes(user?.id))}
                onClose={() => { setCreating(false); setEditing(null); }}
                onSaved={() => { setCreating(false); setEditing(null); load(); }}
            />
        </div>
    );
}

function DiaryField({ label, value }) {
    return (
        <div className="mt-2">
            <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">{label}</div>
            <div className="mt-0.5 whitespace-pre-line text-sm text-mes-text">{value}</div>
        </div>
    );
}

function DiaryDialog({ open, mode, entry, projects, onClose, onSaved }) {
    const [projectId, setProjectId] = useState("");
    const [date, setDate] = useState(todayBerlin());
    const [workPerformed, setWorkPerformed] = useState("");
    const [blockers, setBlockers] = useState("");
    const [additional, setAdditional] = useState("");
    const [agreements, setAgreements] = useState("");
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (mode === "edit" && entry) {
            setProjectId(entry.project_id);
            setDate(entry.date);
            setWorkPerformed(entry.work_performed || "");
            setBlockers(entry.blockers || "");
            setAdditional(entry.additional_work || "");
            setAgreements(entry.agreements || "");
            setPhotos(entry.photos || []);
        } else if (mode === "create") {
            setProjectId(projects[0]?.id || "");
            setDate(todayBerlin());
            setWorkPerformed(""); setBlockers(""); setAdditional(""); setAgreements("");
            setPhotos([]);
        }
    }, [mode, entry, projects, open]);

    const handleUpload = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            for (const f of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/photos/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setPhotos((p) => [...p, { photo_id: data.photo_id, caption: "" }]);
            }
            toast.success("Foto hochgeladen");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setUploading(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            if (mode === "create") {
                await api.post("/diary", {
                    project_id: projectId, date,
                    work_performed: workPerformed, blockers,
                    additional_work: additional, agreements, photos,
                });
                toast.success("Bautagebuch angelegt");
            } else {
                await api.put(`/diary/${entry.id}`, {
                    work_performed: workPerformed, blockers,
                    additional_work: additional, agreements, photos,
                });
                toast.success("Aktualisiert");
            }
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setSaving(false);
    };

    const setField = (setter) => (t) => setter((v) => (v ? v + " " + t : t));

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="diary-dialog" className="max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{mode === "edit" ? "Bautagebuch bearbeiten" : "Neuer Bautageseintrag"}</DialogTitle>
                    <DialogDescription>Notizen, Fotos und anwesende Mitarbeiter für einen Bautag.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {mode === "create" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Projekt</Label>
                                <Select value={projectId} onValueChange={setProjectId}>
                                    <SelectTrigger data-testid="diary-project" className="h-11"><SelectValue placeholder="Projekt" /></SelectTrigger>
                                    <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Datum</Label>
                                <Input data-testid="diary-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11" />
                            </div>
                        </div>
                    )}
                    <FieldWithMic label="Ausgeführte Arbeiten" testid="diary-work" value={workPerformed}
                        onChange={setWorkPerformed} onTranscript={setField(setWorkPerformed)} rows={3} />
                    <FieldWithMic label="Behinderungen / Wartezeiten" testid="diary-blockers" value={blockers}
                        onChange={setBlockers} onTranscript={setField(setBlockers)} rows={2} />
                    <FieldWithMic label="Zusatzarbeiten" testid="diary-additional" value={additional}
                        onChange={setAdditional} onTranscript={setField(setAdditional)} rows={2} />
                    <FieldWithMic label="Vereinbarungen" testid="diary-agreements" value={agreements}
                        onChange={setAgreements} onTranscript={setField(setAgreements)} rows={2} />

                    <div>
                        <div className="flex items-center justify-between">
                            <Label>Fotos ({photos.length})</Label>
                            <PhotoPicker onFiles={handleUpload} disabled={uploading} />
                        </div>
                        {uploading && <div className="mt-1 text-xs text-mes-muted">Foto wird hochgeladen…</div>}
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            {photos.map((p, idx) => (
                                <div key={p.photo_id} className="relative rounded-md border border-mes-border overflow-hidden" data-testid={`photo-${p.photo_id}`}>
                                    <AuthImage photoId={p.photo_id} className="h-24 w-full object-cover" />
                                    <button type="button" data-testid={`btn-remove-photo-${p.photo_id}`}
                                        onClick={() => setPhotos((ps) => ps.filter((_, i) => i !== idx))}
                                        className="absolute right-1 top-1 rounded-full bg-mes-danger p-1 text-white hover:opacity-90">
                                        <X className="h-3 w-3" />
                                    </button>
                                    <input type="text" placeholder="Bildunterschrift" value={p.caption}
                                        onChange={(e) => setPhotos((ps) => ps.map((x, i) => i === idx ? { ...x, caption: e.target.value } : x))}
                                        className="block w-full border-t border-mes-border bg-white px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-mes-brand" />
                                </div>
                            ))}
                            {photos.length === 0 && (
                                <div className="col-span-3 flex flex-col items-center rounded-md border border-dashed border-mes-border py-6 text-mes-muted">
                                    <ImageIcon className="mb-1 h-6 w-6" />
                                    <span className="text-xs">Noch keine Fotos</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="diary-cancel">Abbrechen</Button>
                    <Button data-testid="diary-save" onClick={save} disabled={saving || !projectId}
                        className="mes-btn-primary">Speichern</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldWithMic({ label, testid, value, onChange, onTranscript, rows }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <Label>{label}</Label>
                <MicRecorder testid={`mic-${testid}`} onTranscript={onTranscript} />
            </div>
            <Textarea data-testid={testid} rows={rows} value={value}
                onChange={(e) => onChange(e.target.value)} className="mt-1" />
        </div>
    );
}
